import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, signal } from '@angular/core';
import { AngularModule } from '../../../../modules/angular.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { Client, Location, _ } from '@mira-avant/mtlibs';
import { LocationService } from '../../../../services/location.service';

interface ClientLocation {
	client: Client;
	locations: Location[];
}

@Component({
	selector: 'app-locations-selector',
	standalone: true,
	imports: [AngularModule, ZorroModule],
	templateUrl: './locations-selector.component.html',
	styleUrl: './locations-selector.component.scss',
})
export class LocationsSelectorComponent implements OnChanges {
	@Input() public selectedLocationUuids: string[] = [];
	@Input() public selectedClients: Client[] = [];
	@Output() public onChange: EventEmitter<Location[]> = new EventEmitter<Location[]>();

	public allClientLocations = signal<ClientLocation[]>([]);
	public loading = signal(false);

	private debouncedUpdateLocations: () => void = _.debounce(() => {
		this.updateLocations();
	}, 1000);

	constructor(public locationService: LocationService) {}

	async ngOnChanges(changes: SimpleChanges): Promise<void> {
		if (!this.selectedClients || this.selectedClients.length === 0) {
			this.allClientLocations.set([]);
			return;
		}

		const didClientsChange =
			changes['selectedClients'] &&
			JSON.stringify(changes['selectedClients'].previousValue?.map((c: Client) => c.uuid).sort()) !==
				JSON.stringify(changes['selectedClients'].currentValue?.map((c: Client) => c.uuid).sort());

		if (!didClientsChange) {
			return;
		}

		this.debouncedUpdateLocations();
	}

	private async updateLocations(): Promise<void> {
		if (this.loading()) {
			return;
		}

		this.loading.set(true);

		try {
			const newClientLocations: ClientLocation[] = [];

			for (const client of this.selectedClients) {
				const locations = await this.locationService.getByClient$(client.uuid, 0, 100);
				newClientLocations.push({ client, locations });
			}

			this.allClientLocations.set(newClientLocations);
		} catch (error) {
			console.error('Error loading locations:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public onSelectionChange(newSelectedLocationIds: string[]): void {
		const selectedLocations = this.allClientLocations().flatMap(clientLocation =>
			clientLocation.locations.filter(location => newSelectedLocationIds.includes(location.uuid))
		);
		this.onChange.emit(selectedLocations);
	}
}
